import React, { useState, useEffect } from "react";
import {
  ResponsiveContainer,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import http from "../../Utils/axiosConfig";
import { toISODate } from "../../Utils/commonFuntion";
import "./Analytics.css";

const fmt = (v) => (v != null ? Number(v).toFixed(2) : "0.00");

const TYPE_LABEL = { cow: "गाय", buffalo: "म्हैस" };
const TYPE_COLOR = { cow: "#facc15", buffalo: "#3b82f6" };

// Mirrors CATEGORIES in the server's dairy-expense.service.js and the labels in
// Components/DairyExpense.js.
const EXPENSE_LABEL = {
  electricity: "वीज बिल",
  transport: "वाहतूक",
  salary: "पगार",
  repair: "दुरुस्ती",
  feed: "पशुखाद्य खरेदी",
  construction: "बांधकाम",
  other: "इतर",
};
const EXPENSE_COLORS = ["#f87171", "#fb923c", "#facc15", "#4ade80", "#38bdf8", "#a78bfa", "#94a3b8"];

const Analytics = () => {
  const today = toISODate();
  const firstOfMonth = toISODate(new Date(new Date().getFullYear(), new Date().getMonth(), 1));

  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(today);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [overview, setOverview] = useState(null);
  const [typeSplit, setTypeSplit] = useState([]);
  const [topSuppliers, setTopSuppliers] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [expenses, setExpenses] = useState(null);
  const [income, setIncome] = useState(null);

  const fetchData = async (f = from, t = to) => {
    setLoading(true);
    try {
      const res = await http.post("analytics/dashboard", { from_date: f, to_date: t });
      if (res?.code === "DATA_FOUND") {
        setOverview(res.data.overview);
        setTypeSplit(res.data.typeSplit);
        setTopSuppliers(res.data.topSuppliers);
        setRevenue(res.data.revenue);
        setExpenses(res.data.expenses);
        setIncome(res.data.income);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      setSearched(true);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const expensePieData = (expenses?.byCategory || []).map((row, i) => ({
    name: EXPENSE_LABEL[row.category] || row.category,
    value: row.amount,
    color: EXPENSE_COLORS[i % EXPENSE_COLORS.length],
  }));

  const pieData = typeSplit.map((row) => ({
    name: TYPE_LABEL[row.type] || row.type,
    value: row.total_milk,
    color: TYPE_COLOR[row.type] || "#94a3b8",
  }));

  return (
    <div className="an-container">
      {/* Filter bar */}
      <div className="an-filter-bar">
        <div className="an-filter-group">
          <label>पासून</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="an-filter-group">
          <label>पर्यंत</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <button className="an-search-btn" onClick={() => fetchData()} disabled={loading}>
          {loading ? "..." : "शोधा"}
        </button>
      </div>

      {searched && !overview ? (
        <div className="an-no-data">
          <p>या कालावधीसाठी नोंदी उपलब्ध नाहीत.</p>
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="an-card-row">
            <div className="an-card">
              <span className="an-card-label">एकूण दूध (लि.)</span>
              <span className="an-card-value">{fmt(overview?.total_milk)}</span>
            </div>
            <div className="an-card">
              <span className="an-card-label">एकूण रक्कम (₹)</span>
              <span className="an-card-value an-green">{fmt(overview?.total_amount)}</span>
            </div>
            <div className="an-card">
              <span className="an-card-label">सरासरी फॅट %</span>
              <span className="an-card-value">{fmt(overview?.avg_fat)}</span>
            </div>
            <div className="an-card">
              <span className="an-card-label">सरासरी SNF %</span>
              <span className="an-card-value">{fmt(overview?.avg_snf)}</span>
            </div>
            <div className="an-card">
              <span className="an-card-label">एकूण सभासद</span>
              <span className="an-card-value">{overview?.total_members ?? 0}</span>
            </div>
            <div className="an-card">
              <span className="an-card-label">सक्रिय सभासद</span>
              <span className="an-card-value">{overview?.active_members ?? 0}</span>
            </div>
          </div>

          {/* Revenue cards */}
          <div className="an-card-row">
            <div className="an-card">
              <span className="an-card-label">ॲडव्हान्स दिलेली</span>
              <span className="an-card-value">{fmt(revenue?.advance?.given)}</span>
            </div>
            <div className="an-card">
              <span className="an-card-label">ॲडव्हान्स वसूल</span>
              <span className="an-card-value an-green">{fmt(revenue?.advance?.recovered)}</span>
            </div>
            <div className="an-card">
              <span className="an-card-label">पशुखाद्य दिलेले</span>
              <span className="an-card-value">{fmt(revenue?.supply?.given)}</span>
            </div>
            <div className="an-card">
              <span className="an-card-label">पशुखाद्य वसूल</span>
              <span className="an-card-value an-green">{fmt(revenue?.supply?.recovered)}</span>
            </div>
            <div className="an-card">
              <span className="an-card-label">बँक</span>
              <span className="an-card-value">{fmt(revenue?.bank)}</span>
            </div>
            <div className="an-card">
              <span className="an-card-label">एकूण पेमेंट</span>
              <span className="an-card-value an-green">{fmt(revenue?.grand_total)}</span>
            </div>
            <div className="an-card">
              <span className="an-card-label">एकूण इतर खर्च</span>
              <span className="an-card-value an-red">{fmt(expenses?.grand)}</span>
            </div>
          </div>

          {/* Net — the arithmetic is laid out rather than given as one figure, so the
              dairy can see where it came from. Expenses feed only this panel; the
              Dairy Register's नफा/तोटा is untouched. */}
          <div className="an-panel">
            <h3 className="an-panel-title">निव्वळ</h3>
            <div className="an-net">
              <div className="an-net-row">
                <span>संघ पावती</span><span>₹{fmt(income?.sangha_receipt)}</span>
              </div>
              <div className="an-net-row">
                <span>स्थानिक विक्री</span><span>₹{fmt(income?.local_sale)}</span>
              </div>
              <div className="an-net-row an-net-row--sub">
                <span>एकूण उत्पन्न</span><span>₹{fmt(income?.total)}</span>
              </div>
              <div className="an-net-row an-net-minus">
                <span>सभासद पेमेंट</span><span>− ₹{fmt(income?.member_payment)}</span>
              </div>
              <div className="an-net-row an-net-minus">
                <span>इतर खर्च</span><span>− ₹{fmt(income?.expenses)}</span>
              </div>
              <div className="an-net-row an-net-row--total">
                <span>निव्वळ</span>
                <span className={(income?.net ?? 0) < 0 ? "an-red" : "an-green"}>
                  ₹{fmt(income?.net)}
                </span>
              </div>
            </div>
            <p className="an-net-note">
              सभासद पेमेंट फक्त तयार झालेल्या बिलांचे आहे — या कालावधीचे बिल तयार नसेल तर
              निव्वळ जास्त दिसेल.
            </p>
          </div>

          {/* Cow/Buffalo split */}
          <div className="an-panel">
            <h3 className="an-panel-title">गाय / म्हैस दूध विभागणी</h3>
            <div className="an-chart-wrap">
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                    {pieData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#00305A", border: "1px solid rgba(255,255,255,0.2)" }} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Expense split */}
          {expensePieData.length > 0 && (
            <div className="an-panel">
              <h3 className="an-panel-title">इतर खर्च विभागणी</h3>
              <div className="an-chart-wrap">
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={expensePieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                      {expensePieData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#00305A", border: "1px solid rgba(255,255,255,0.2)" }} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Top suppliers */}
          <div className="an-panel">
            <h3 className="an-panel-title">सर्वाधिक दूध पुरवठादार</h3>
            <div className="an-table-wrap">
              <table className="an-table">
                <thead>
                  <tr>
                    <th>क्र.</th>
                    <th>क्रमांक</th>
                    <th>नाव</th>
                    <th>दूध (लि.)</th>
                    <th>रक्कम (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {topSuppliers.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="an-no-data-cell">
                        नोंदी उपलब्ध नाहीत.
                      </td>
                    </tr>
                  ) : (
                    topSuppliers.map((row, i) => (
                      <tr key={row.mem_id}>
                        <td>{i + 1}</td>
                        <td>{row.dairy_id}</td>
                        <td className="an-name">{row.mr_name || row.en_name}</td>
                        <td>{fmt(row.total_milk)}</td>
                        <td>{fmt(row.total_amount)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Analytics;
